import { AxiosError } from 'axios'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import React from 'react'
import * as yup from 'yup'
import { DayOfWeekKey, DaySpec, Facility, FacilitySchedule, HoursOfOperation } from 'src/api/models'
import { HoursOfOperationField, NumberField, useForm } from 'src/forms'
import { setApiErrors } from 'src/forms/errors'
import { skuit } from 'src/services/skuit'
import { handleErrorMessage, Notification } from 'src/components/Notification'
import toast from 'react-hot-toast'

const useStyles = makeStyles(() => ({
  form: {
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
}))

interface FacilityScheduleProps {
  max_inbound: number // eslint-disable-line camelcase
  max_outbound: number // eslint-disable-line camelcase
  default: DaySpec
  sun?: DaySpec
  mon?: DaySpec
  tue?: DaySpec
  wed?: DaySpec
  thu?: DaySpec
  fri?: DaySpec
  sat?: DaySpec
  hours_of_operation?: HoursOfOperation // eslint-disable-line camelcase
}

const scheduleSchema = yup.object().shape({
  max_inbound: yup.number().required().label('Max Inbound Appointments'),
  max_outbound: yup.number().required().label('Max Outbound Appointments'),
  default: yup.object().hoursOfOperation().optional(),
  sun: yup.object().hoursOfOperation().optional(),
  mon: yup.object().hoursOfOperation().optional(),
  tue: yup.object().hoursOfOperation().optional(),
  wed: yup.object().hoursOfOperation().optional(),
  thu: yup.object().hoursOfOperation().optional(),
  fri: yup.object().hoursOfOperation().optional(),
  sat: yup.object().hoursOfOperation().optional(),
})

interface Props {
  open: boolean
  handleClose: () => void
  onSave?: (schedule: FacilitySchedule) => void
  facility?: Facility
  schedule?: FacilitySchedule
}

type DayTuple = [DayOfWeekKey, string]

const days: DayTuple[] = [
  ['default', 'Default'],
  ['sun', 'Sunday'],
  ['mon', 'Monday'],
  ['tue', 'Tuesday'],
  ['wed', 'Wednesday'],
  ['thu', 'Thursday'],
  ['fri', 'Friday'],
  ['sat', 'Saturday'],
]

export const FacilityScheduleDialog = (props: Props) => {
  const { open, handleClose, onSave, facility, schedule } = props
  const classes = useStyles()

  const onSubmit = (data: FacilityScheduleProps) => {
    const func = (data: FacilityScheduleProps) =>
      skuit.sc.facilities.schedule.patch(facility?.guid || '', data)
    const action = 'updat'
    const hoursOfOperation: HoursOfOperation = { default: data.default }
    for (const [key] of days) {
      const daySpec = data[key]
      if (daySpec && daySpec.open !== undefined && daySpec.close !== undefined) {
        hoursOfOperation[key] = daySpec
        delete data[key]
      }
    }
    data.hours_of_operation = hoursOfOperation // eslint-disable-line camelcase
    func(data)
      .then((newFacilitySchedule: FacilitySchedule) => {
        handleClose()
        onSave?.(newFacilitySchedule)
        toast.success(() => (
          <Notification
            message={`Successfully ${action}ed FacilitySchedule`}
            newObject={newFacilitySchedule}
          />
        ))
      })
      .catch((error: AxiosError) => {
        handleErrorMessage(error, action, 'FacilitySchedule')
        setApiErrors(formData.setError, error)
      })
  }

  const { Form, data: formData } = useForm(scheduleSchema)

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <Form formProps={{ className: classes.form }} onSubmit={onSubmit}>
        <DialogTitle>Update FacilitySchedule</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <NumberField
                name="max_inbound"
                label="Max Inbound Appointments"
                defaultValue={schedule?.maxInbound}
              />
            </Grid>
            <Grid item xs={12}>
              <NumberField
                name="max_outbound"
                label="Max Outbound Appointments"
                defaultValue={schedule?.maxOutbound}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6">Hours of Operation</Typography>
            </Grid>
            {days.map(([field, day]) => (
              <HoursOfOperationField
                key={field}
                name={field}
                label={day}
                defaultValue={schedule?.hoursOfOperation[field as DayOfWeekKey]}
              />
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button type="submit" color="primary" variant="contained" autoFocus>
            Save
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  )
}
